import { Component, computed, DestroyRef, effect, EffectRef, ElementRef, inject, signal, Signal, ViewChild, WritableSignal } from '@angular/core';
import { ButtonComponent } from '@components/shared/button/button.component';
import { ButtonType } from '@models/enums/components/Button/ButtonType.enum';
import { AppViews } from '@models/enums/dvm/DvmViews.enum';
import { MobileButtonService } from '@services/components/mobile-button.service';
import { DvmService } from '@services/dvm.service';
import { APP_CONFIG } from '@configuration/configuration';
import { Configuration } from '@models/interfaces/configuration/index.interface';
import { isInIframe } from '@helpers/utils/is-in-iframe.util';
import anime from 'animejs';

@Component({
    selector: 'app-mobile-button',
    imports: [
        ButtonComponent
    ],
    templateUrl: './mobile-button.component.html',
    styleUrl: './mobile-button.component.css',
    // In the iframe topbar (no gradient behind it) the burger rests on the brand color.
    host: { '[class.on-brand]': 'iframeMode' }
})
export class MobileButtonComponent {

  // VIEW CHILDREN
  @ViewChild('mobileBtn', { read: ElementRef })
  private readonly mobileBtn!:  ElementRef<ButtonComponent>;

  // SERVICES
  private readonly view:        Signal<AppViews| null> = inject(DvmService).view;
  private readonly destroyRef:  DestroyRef             = inject(DestroyRef);
  public  readonly service:     MobileButtonService    = inject(MobileButtonService);
  private readonly config:      Signal<Configuration>  = inject(APP_CONFIG);

  // The burger only opens the action buttons, so it's pointless without any configured.
  public readonly hasActionButtons: Signal<boolean> = computed((): boolean => {
    const ui = this.config().Ui;
    return ((ui.ActionButtons?.length ?? 0) + (ui.LinkActionButtons?.length ?? 0)) > 0;
  });

  // STATE
  protected readonly iframeMode: boolean    = isInIframe();
  public readonly showElement:  WritableSignal<boolean> = signal(false);
  public          isActive:     boolean     = false;
  public readonly buttonType:   ButtonType  = ButtonType.Pill;
  private animationTimer:       ReturnType<typeof setTimeout> | null = null;

  // EFFECT
  public readonly toggleVisibility: EffectRef = effect((): void=> {

    if(this.view() === null || !this.hasActionButtons()) return;

    if(this.showElement()) this.animateButton();
    else {
      this.clearAnimationTimer();
      this.animationTimer = window.setTimeout((): void => {
        this.animateButton();
        this.animationTimer = null;
      }, 300);
    }

  })

  constructor() {
    this.destroyRef.onDestroy((): void => this.clearAnimationTimer());
  }

  private animateButton(): void {

    anime({
      begin:    (): void => { if(!this.showElement()) this.showElement.set(true); },
      targets:  this.mobileBtn.nativeElement,
      opacity:  1,
      duration: 300,
      easing:   'easeInOutQuad',
    })

  }

  private clearAnimationTimer(): void {
    if(!this.animationTimer) return;
    clearTimeout(this.animationTimer);
    this.animationTimer = null;
  }

}
